import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Marker we stamp on `history.state` for the navigation-guard sentinel entry
 * so we can detect whether a sentinel is still on top of the back-stack
 * across effect re-runs and listener tear-downs. Using `history.state`
 * (rather than a component-level ref) means we correctly observe sentinel
 * consumption that happens while listeners are detached, e.g. when
 * `isUpdatingOrder` briefly flips `shouldBlockNavigation` to false during a
 * Send Updates round-trip and the user presses browser-back in that window.
 */
const NAV_GUARD_SENTINEL_KEY = '__inflectionUnsavedChangesSentinel'

const isNavGuardSentinelOnTop = (): boolean => {
  const s = window.history.state as Record<string, unknown> | null
  return Boolean(s && s[NAV_GUARD_SENTINEL_KEY] === true)
}

export interface UnsavedChangesNavGuard {
  /** Whether the "unsaved changes" confirmation modal should render. */
  showUnsavedChangesModal: boolean
  /** Dismiss the modal and abandon the queued navigation (stay on page). */
  handleKeepEditing: () => void
  /** Dismiss the modal and run the queued navigation (leave the page). */
  handleDiscardChanges: () => void
}

/**
 * Navigation guard for unsent edits. When `shouldBlockNavigation` is true, four
 * exit paths are intercepted so the user can confirm before losing canonical
 * state that hasn't been pushed to the lab:
 *   1. Anchor element clicks (capture-phase listener) - covers <Link>
 *      and <NavLink> from react-router as well as plain <a> tags.
 *   2. Programmatic navigation via `history.pushState`/`replaceState`
 *      (react-router-dom calls these under the hood for `useNavigate`).
 *      Required because the Navbar logo, logout button, and other
 *      button-driven nav points are not anchors.
 *   3. Browser back / forward (sentinel `pushState` + `popstate`).
 *   4. Full-page exits (`beforeunload`).
 *
 * We can't use `useBlocker` from react-router-dom because the app is
 * mounted inside the legacy `<BrowserRouter>` (not a data router). The
 * manual approach below is router-agnostic.
 */
export function useUnsavedChangesNavGuard(shouldBlockNavigation: boolean): UnsavedChangesNavGuard {
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)

  /** Set true while we re-issue a navigation that the user confirmed via
   *  the modal, so the click/popstate handlers don't re-block themselves. */
  const bypassNavGuardRef = useRef(false)
  /** Action queued when the modal opens; executed if the user confirms
   *  "Discard Changes". `null` means a popstate-style "go back". */
  const pendingNavigationRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (!shouldBlockNavigation) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Required for older browsers; modern browsers ignore the value.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    const handleAnchorClick = (event: MouseEvent) => {
      if (bypassNavGuardRef.current) return
      // Modifier keys / non-primary button = open in new tab/window — let it through.
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const anchor = (event.target as HTMLElement | null)?.closest('a')
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href) return
      if (anchor.target && anchor.target !== '' && anchor.target !== '_self') return
      // Only block same-origin internal navigation.
      const url = new URL(href, window.location.href)
      if (url.origin !== window.location.origin) return
      // Same in-page anchor — don't block.
      if (url.pathname === window.location.pathname && url.search === window.location.search) return

      event.preventDefault()
      event.stopPropagation()
      pendingNavigationRef.current = () => {
        bypassNavGuardRef.current = true
        anchor.click()
        rearmBypassAfterNav()
      }
      setShowUnsavedChangesModal(true)
    }
    document.addEventListener('click', handleAnchorClick, true)

    // Monkey-patch history methods to catch programmatic navigation from
    // button/onClick handlers (Navbar logo, etc.) that go through
    // `useNavigate`. We restore the originals on cleanup.
    const originalPushState = window.history.pushState.bind(window.history)
    const originalReplaceState = window.history.replaceState.bind(window.history)
    const samePathAsCurrent = (url?: string | URL | null) => {
      if (!url) return true
      const next = new URL(url.toString(), window.location.href)
      return (
        next.origin === window.location.origin &&
        next.pathname === window.location.pathname &&
        next.search === window.location.search
      )
    }
    window.history.pushState = function patchedPushState(data: unknown, unused: string, url?: string | URL | null) {
      if (bypassNavGuardRef.current || samePathAsCurrent(url)) {
        return originalPushState(data, unused, url ?? null)
      }
      pendingNavigationRef.current = () => {
        bypassNavGuardRef.current = true
        originalPushState(data, unused, url ?? null)
        // React Router listens to popstate; dispatch one so it picks up
        // the URL change and re-renders the matched route.
        window.dispatchEvent(new PopStateEvent('popstate'))
        rearmBypassAfterNav()
      }
      setShowUnsavedChangesModal(true)
    }
    window.history.replaceState = function patchedReplaceState(
      data: unknown,
      unused: string,
      url?: string | URL | null
    ) {
      if (bypassNavGuardRef.current || samePathAsCurrent(url)) {
        return originalReplaceState(data, unused, url ?? null)
      }
      pendingNavigationRef.current = () => {
        bypassNavGuardRef.current = true
        originalReplaceState(data, unused, url ?? null)
        window.dispatchEvent(new PopStateEvent('popstate'))
        rearmBypassAfterNav()
      }
      setShowUnsavedChangesModal(true)
    }

    /**
     * Re-arms `bypassNavGuardRef` after a confirmed-discard navigation.
     * The bypass must stay true long enough for the synthetic popstate /
     * router work to flush so our own listeners don't re-block. If the nav
     * succeeds, the component unmounts before this fires (write to a dead
     * ref is harmless). If the nav silently fails (e.g. `history.go(-2)`
     * on a shallow-history tab opened directly), this re-enables the guard
     * so the user isn't left with a permanently disabled guard and a
     * latent data-loss bug.
     */
    const rearmBypassAfterNav = () => {
      window.setTimeout(() => {
        bypassNavGuardRef.current = false
      }, 100)
    }

    // Sentinel state on the current history entry so the next browser-back
    // fires popstate without actually navigating. We mark the entry with a
    // unique field on `history.state` and use that as the source of truth
    // for whether a sentinel is currently on top. This survives the listener
    // being briefly torn down (e.g. while `isUpdatingOrder` flips
    // `shouldBlockNavigation` to false during Send Updates) and correctly
    // detects the case where the browser silently consumed the sentinel
    // during that window — the next setup pushes a fresh one instead of
    // skipping based on a stale ref.
    const pushSentinelIfMissing = () => {
      if (isNavGuardSentinelOnTop()) return
      const carriedState = (window.history.state as Record<string, unknown> | null) ?? {}
      originalPushState({ ...carriedState, [NAV_GUARD_SENTINEL_KEY]: true }, '', window.location.href)
    }
    pushSentinelIfMissing()
    const handlePopState = () => {
      if (bypassNavGuardRef.current) return
      // Browser just consumed our sentinel; re-push so the next back is
      // also absorbed. Net depth on the history stack stays at 1.
      pushSentinelIfMissing()
      pendingNavigationRef.current = () => {
        bypassNavGuardRef.current = true
        // Need to pop twice: once to undo the sentinel we just pushed, once
        // for the original entry the user wanted to leave to. On a shallow
        // history (e.g. order page opened directly in a new tab) this is
        // a silent no-op; `rearmBypassAfterNav` re-enables the guard so
        // the user isn't stuck with all exits silently disabled.
        window.history.go(-2)
        rearmBypassAfterNav()
      }
      setShowUnsavedChangesModal(true)
    }
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('click', handleAnchorClick, true)
      window.removeEventListener('popstate', handlePopState)
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
      // Intentionally don't pop the sentinel here: programmatically
      // back-traversing during cleanup races with React Router's own
      // popstate listener. The `isNavGuardSentinelOnTop()` check on the
      // next setup is the real safeguard against duplicates - it reads
      // `history.state` directly, so it correctly sees a sentinel that
      // was consumed by the user pressing back while listeners were off.
      // Worst case: one stale same-URL history entry remains after a
      // successful Send Updates, costing one wasted back-press.
    }
  }, [shouldBlockNavigation])

  const handleKeepEditing = useCallback(() => {
    pendingNavigationRef.current = null
    setShowUnsavedChangesModal(false)
  }, [])

  const handleDiscardChanges = useCallback(() => {
    const pending = pendingNavigationRef.current
    pendingNavigationRef.current = null
    setShowUnsavedChangesModal(false)
    if (pending) pending()
  }, [])

  return { showUnsavedChangesModal, handleKeepEditing, handleDiscardChanges }
}
